/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import goodNewsGarageLogo from "@/assets/images/good-news-garage-logo.png";
import heroGarageImage1 from "@/assets/images/hero-garage-image-1-optimized.jpg";
import heroGarageImage2 from "@/assets/images/hero-garage-image-2-optimized.jpg";
import staffMechanics from "@/assets/images/staff-mechanics.jpg";
import goodNewsGarageFloorCars from "@/assets/images/good-news-garage-floor-cars.jpg";
import mechanicsTalking from "@/assets/images/mechanics-talking.jpg";
import tireRepairMechanic from "@/assets/images/tire-repair-mechanic.jpg";
import vehicleCareSpecialist from "@/assets/images/vehicle-care-specialist.jpg";
import carTalkLogo from "@/assets/images/car-talk-logo.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Good News Garage logo - multiple path variations for flexibility
  "/wp-content/uploads/2019/04/Good-News-Garage-logo_237.png": goodNewsGarageLogo,
  "/assets/images/good-news-garage-logo.png": goodNewsGarageLogo,
  "/images/good-news-garage-logo.png": goodNewsGarageLogo,
  "good-news-garage-logo.png": goodNewsGarageLogo,

  // Hero garage images - original crawl paths and clean paths
  "/wp-content/uploads/2019/08/DSC0396.jpeg": heroGarageImage1,
  "/assets/images/hero-garage-image-1.jpg": heroGarageImage1,
  "/images/hero-garage-image-1.jpg": heroGarageImage1,
  "hero-garage-image-1.jpg": heroGarageImage1,

  "/wp-content/uploads/2019/08/DSC0353.jpeg": heroGarageImage2,
  "/wp-content/uploads/2019/03/Ray-Magliozzi-in-car-talking-768x512.jpg": heroGarageImage2,
  "/assets/images/hero-garage-image-2.jpg": heroGarageImage2,
  "/images/hero-garage-image-2.jpg": heroGarageImage2,
  "hero-garage-image-2.jpg": heroGarageImage2,

  // Staff mechanics photo
  "/wp-content/uploads/2019/02/good-news-garage-staff-mechanics.jpg": staffMechanics,
  "/wp-content/uploads/2019/03/good-news-garage-staff-mechanics-workbench.jpg": staffMechanics,
  "/assets/images/staff-mechanics.jpg": staffMechanics,
  "/images/staff-mechanics.jpg": staffMechanics,
  "staff-mechanics.jpg": staffMechanics,

  // Services page images
  "/wp-content/uploads/2019/03/good-news-garage-floor-cars.jpg": goodNewsGarageFloorCars,
  "/assets/images/good-news-garage-floor-cars.jpg": goodNewsGarageFloorCars,
  "/images/good-news-garage-floor-cars.jpg": goodNewsGarageFloorCars,
  "good-news-garage-floor-cars.jpg": goodNewsGarageFloorCars,

  "/wp-content/uploads/2019/03/mechanics-talking.jpg": mechanicsTalking,
  "/wp-content/uploads/2019/03/ray-owner-another.jpg": mechanicsTalking,
  "/wp-content/uploads/2019/03/ray-owner-another-768x512.jpg": mechanicsTalking,
  "/assets/images/mechanics-talking.jpg": mechanicsTalking,
  "/images/mechanics-talking.jpg": mechanicsTalking,
  "mechanics-talking.jpg": mechanicsTalking,

  "/wp-content/uploads/2019/03/tire-repair-mechanic2.jpg": tireRepairMechanic,
  "/wp-content/uploads/2019/03/team-work-auto-repair-768x512.jpg": tireRepairMechanic,
  "/assets/images/tire-repair-mechanic.jpg": tireRepairMechanic,
  "/images/tire-repair-mechanic.jpg": tireRepairMechanic,
  "tire-repair-mechanic.jpg": tireRepairMechanic,

  // Vehicle care specialist photo - Our Services section
  "/wp-content/uploads/2019/02/good-news-garage-vehicle-care-specialist.jpg": vehicleCareSpecialist,
  "/wp-content/uploads/2019/02/good-news-garage-vehicle-care-specialist-768x512.jpg": vehicleCareSpecialist,
  "/wp-content/uploads/2019/02/good-news-garage-vehicle-care-specialist-1024x683.jpg": vehicleCareSpecialist,
  "/assets/images/vehicle-care-specialist.jpg": vehicleCareSpecialist,
  "/images/vehicle-care-specialist.jpg": vehicleCareSpecialist,
  "vehicle-care-specialist.jpg": vehicleCareSpecialist,

  // Car Talk logo for promotion section
  "/wp-content/uploads/2019/04/car-talk-300x213.jpg": carTalkLogo,
  "/wp-content/uploads/2019/04/car-talk.jpg": carTalkLogo,
  "/assets/images/car-talk-logo.jpg": carTalkLogo,
  "/images/car-talk-logo.jpg": carTalkLogo,
  "car-talk-logo.jpg": carTalkLogo,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
